import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type MarketplaceDirectoryAccessJSON = z.infer<typeof MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessJSONZ>;

export type MarketplaceDirectoryAccessParams = z.infer<typeof MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessZ>;

export default class MarketplaceDirectoryAccessSchema {
  public static readonly MarketplaceDirectoryAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.MarketplaceDirectoryAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly MarketplaceDirectoryAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.MarketplaceDirectoryAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): MarketplaceDirectoryAccessParams {
    return parseOrThrow(MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessZ, values, {
      logError: true,
      schemaName: "MarketplaceDirectoryAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): MarketplaceDirectoryAccessJSON {
    return parseOrThrow(MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessJSONZ, values, {
      logError: true,
      schemaName: "MarketplaceDirectoryAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is MarketplaceDirectoryAccessParams {
    try {
      MarketplaceDirectoryAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is MarketplaceDirectoryAccessJSON {
    try {
      MarketplaceDirectoryAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: MarketplaceDirectoryAccessJSON): MarketplaceDirectoryAccessParams {
    const parsed = parseOrThrow(MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessJSONZ, data, {
      logError: true,
      schemaName: "MarketplaceDirectoryAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return MarketplaceDirectoryAccessSchema.codec.decode(parsed);
  }

  public static serialize(resource: MarketplaceDirectoryAccessParams): MarketplaceDirectoryAccessJSON {
    const parsed = MarketplaceDirectoryAccessSchema.validate(resource);
    return MarketplaceDirectoryAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessJSONZ,
    MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.MarketplaceDirectoryAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.MarketplaceDirectoryAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
